// @givmo/app-admin — employer gift-card RECIPIENT picker (EP-WEB4).
//
// A SINGLE-select picker over the company roster (`GET /admin/company/employees` via `useInfiniteQuery`
// — the employees-page pattern) with the searchable single-select UX of the give-page target picker.
// The recipient is addressed by `work_email` (the backend delivery address + `recipient_work_email`
// body field, which 422s off-roster), so an employee with NO work email on file is shown but DISABLED
// with an explicit "no work email on file" hint — visible-but-disabled beats silent omission (the admin
// sees who they can't gift yet, and why). On pick, the parent receives `{ id, name, workEmail }`.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { sdk, isGivmoError, type CompanyEmployeeOut } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import {
  Avatar,
  Button,
  EmptyState,
  Icon,
  SearchInput,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { Users } from 'lucide-react';
import { employeeFullName } from '../employees/employees-decode';
import type { GiftCardRecipient } from './gift-cards-decode';

const PREFIX = 'gift-card-recipient';
const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;

const LIST_KEY = (q: string) => ['companies-get_company_employees', 'gift-cards', q] as const;

async function fetchEmployeesPage(
  q: string,
  offset: number,
  signal: AbortSignal,
): Promise<CompanyEmployeeOut[]> {
  const trimmed = q.trim();
  const { data } = await sdk.companiesGetCompanyEmployees({
    query: {
      ...(trimmed.length > 0 ? { q: trimmed } : {}),
      'page[limit]': PAGE_LIMIT,
      'page[offset]': offset,
    },
    signal,
    throwOnError: true,
  });
  return extractList<CompanyEmployeeOut>(data);
}

/** The work email an employee is gifted at, or null when none is on file (→ not selectable). */
function recipientWorkEmail(employee: CompanyEmployeeOut): string | null {
  const email = employee.work_email;
  return typeof email === 'string' && email.trim().length > 0 ? email.trim() : null;
}

export interface GiftCardRecipientPickerProps {
  selected: GiftCardRecipient | null;
  onSelect: (recipient: GiftCardRecipient) => void;
  onClear: () => void;
}

export function GiftCardRecipientPicker({
  selected,
  onSelect,
  onClear,
}: GiftCardRecipientPickerProps) {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setQuery(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const list = useInfiniteQuery({
    queryKey: LIST_KEY(query),
    queryFn: ({ pageParam, signal }: QueryFunctionContext<ReturnType<typeof LIST_KEY>, number>) =>
      fetchEmployeesPage(query, pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_LIMIT ? undefined : allPages.reduce((n, p) => n + p.length, 0),
    enabled: selected === null,
  });

  const employees = useMemo(() => (list.data ? list.data.pages.flat() : []), [list.data]);

  // ── Selected recipient chip ──
  if (selected !== null) {
    return (
      <div
        className="flex items-center gap-10 rounded-lg border border-brand-500 bg-surface-sunken p-12"
        {...testIdProp(`${PREFIX}-selected`)}
      >
        <Avatar name={selected.name} size="sm" />
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-w600 text-text-strong">{selected.name}</span>
          <span className="truncate text-s12 text-text-tertiary">{selected.workEmail}</span>
        </div>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          className="ml-auto"
          onClick={onClear}
          testId={`${PREFIX}-clear`}
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-12">
      <SearchInput
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onClear={() => setSearchInput('')}
        placeholder="Search employees by name or email…"
        aria-label="Search recipients"
        testId={`${PREFIX}-search`}
      />

      <RecipientResults
        loading={list.isPending}
        error={list.isError}
        errorMessage={
          isGivmoError(list.error) ? list.error.message : 'Couldn’t load your employees.'
        }
        employees={employees}
        onSelect={onSelect}
        onRetry={() => void list.refetch()}
        hasMore={list.hasNextPage === true}
        loadingMore={list.isFetchingNextPage}
        onLoadMore={() => void list.fetchNextPage()}
        searching={query.trim().length > 0}
      />
    </div>
  );
}

function RecipientResults({
  loading,
  error,
  errorMessage,
  employees,
  onSelect,
  onRetry,
  hasMore,
  loadingMore,
  onLoadMore,
  searching,
}: {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  employees: CompanyEmployeeOut[];
  onSelect: (recipient: GiftCardRecipient) => void;
  onRetry: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  searching: boolean;
}) {
  // Sentinel-driven prefetch of the next page, mirroring the employees page.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasMore && !loadingMore) onLoadMore();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  if (loading) {
    return (
      <ul className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-results`)}>
        {Array.from({ length: 4 }).map((_, i) => (
          <li
            key={i}
            className="flex items-center gap-10 rounded-lg border border-border-subtle p-12"
          >
            <Skeleton className="size-32 shrink-0 rounded-full" />
            <Skeleton className="h-12 flex-1" />
          </li>
        ))}
      </ul>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center gap-8 py-16 text-center">
        <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
          {errorMessage}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
        >
          Try again
        </button>
      </div>
    );
  }
  if (employees.length === 0) {
    return (
      <EmptyState
        testId={`${PREFIX}-empty`}
        icon={<Icon icon={Users} size={20} aria-hidden />}
        title="No employees found"
        description={
          searching
            ? 'No employees match your search. Try a different name or email.'
            : 'Employees who join your company program will appear here.'
        }
      />
    );
  }

  return (
    <>
      <ul className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-results`)}>
        {employees.map((employee) => {
          const name = employeeFullName(employee) || 'Employee';
          const workEmail = recipientWorkEmail(employee);
          const selectable = workEmail !== null;
          return (
            <li key={employee.id}>
              <button
                type="button"
                disabled={!selectable}
                aria-disabled={!selectable}
                onClick={() => {
                  if (workEmail !== null) onSelect({ id: employee.id, name, workEmail });
                }}
                className="flex w-full items-center gap-10 rounded-lg border border-border-subtle p-12 text-left outline-none transition-colors hover:bg-surface-hover focus-visible:border-brand-500 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                {...testIdProp(`${PREFIX}-result-${employee.id}`)}
              >
                <Avatar name={name} src={employee.logo_url ?? undefined} size="sm" />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate font-w600 text-text-strong">{name}</span>
                  {selectable ? (
                    <span className="truncate text-s12 text-text-tertiary">{workEmail}</span>
                  ) : (
                    <span
                      className="truncate text-s12 text-text-tertiary"
                      {...testIdProp(`${PREFIX}-no-email-${employee.id}`)}
                    >
                      No work email on file
                    </span>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <div className="flex items-center justify-center">
          <div ref={sentinelRef} aria-hidden className="size-px" />
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            disabled={loadingMore}
            onClick={onLoadMore}
            testId={`${PREFIX}-load-more`}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </>
  );
}
